import { BookmarkBorderOutlined } from "@mui/icons-material";
import BookmarkOutlinedIcon from '@mui/icons-material/BookmarkOutlined';
import LinChartIcon from "../../../commom/Assets/images/LineChart.png";
import CandleStickIcon from "../../../commom/Assets/images/CandleStick.png";
import {
  Avatar,
  Box,
  Container,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  CardContent,
  CircularProgress,
  Button,
  Tooltip
} from "@mui/material";
import React, { useState, useRef, useCallback } from "react";
import { Card } from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import moment from "moment";
import RightSideSection from "../SharedComponent/RightSideSection";
import LineChart from "../charts/Linechart";
import CandlestickChart from "../charts/CandlestickChart";
import { getCurrentUserId } from "../../../utils/utils";
import { getCustomerInfoFromLocalStorage } from "../../../utils/utils";
import { UrlConstant } from "../../../commom/UrlConstant";
import Axios from "../../../config/axios";
import { startLoading, endLoading } from "../../../Redux/loadingSlice";
import { Crypto } from "../../../types/crypto";
import Successpopup from "../SharedComponent/Successpopup";
import BuySell from "../Cards/BuySell";
import AiAnalysis from "../Popups/AiAnalysis";
import ThunderIconCirlcle from "../SvgIcons/ThunderIconCirlcle";
import { usePriceFormatter } from "../../../hooks";
import { calculateChangeAmount, formatPercentage } from "../../../utils/formatting";
import {
  extractQuoteWithFallback,
  type CryptoData,
} from "../../../helpers/cryptoQuoteHelper";
import { useAppDispatch, useAppSelector } from "../../../Redux/hooks";

const getPeriodForSelection = (selection: string): number => {
  switch (selection) {
    case "1 Day": return 900;
    case "5 Day": return 3600;
    case "1 Month": return 86400;
    case "3 Month": return 86400;
    case "6 Month": return 86400;
    case "1 Year": return 86400;
    case "5 Years": return 86400;
    case "All": return 86400;
    default: return 86400;
  }
};

const getTimeRangeForSelection = (selection: string): { startTime: number; endTime: number } => {
  const now = Math.floor(Date.now() / 1000);
  let startTime = 0;

  switch (selection) {
    case "1 Day": startTime = now - (1 * 24 * 60 * 60); break;
    case "5 Day": startTime = now - (5 * 24 * 60 * 60); break;
    case "1 Month": startTime = now - (30 * 24 * 60 * 60); break;
    case "3 Month": startTime = now - (90 * 24 * 60 * 60); break;
    case "6 Month": startTime = now - (180 * 24 * 60 * 60); break;
    case "1 Year": startTime = now - (365 * 24 * 60 * 60); break;
    case "5 Years": startTime = now - (5 * 365 * 24 * 60 * 60); break;
    case "All": startTime = now - (10 * 365 * 24 * 60 * 60); break;
    default: startTime = now - (30 * 24 * 60 * 60);
  }

  return { startTime, endTime: now };
};

function CryptoDetail() {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  // Get currency data from Redux
  const currencyInfo = useAppSelector((state) => state.currency);

  const [isWatchlist, setIsWatchlist] = useState(false);
  const [selection, setSelection] = useState<string>("1 Month");
  const [expandedCards, setExpandedCards] = useState<{ [key: number]: boolean }>({});
  const scrollRef = useRef<HTMLDivElement>(null);
  const [analysisPopup, setAnalysisPopup] = useState(false);
  const [graphType, setGraphType] = useState<"line" | "candlestick">("line");
  const [chartData, setChartData] = useState<any[]>([]);
  const [trendValue, setTrendValue] = useState<number>(0);
  const [trendPercentage, setTrendPercentage] = useState<number>(0);
  const [userStatusData, setUserStatusData] = useState<any>(null);
  const [candleStickChartData, setCandleStickChartData] = useState<any[]>([]);
  const [crypto, setCrypto] = useState<Crypto | null>(null);
  const [loading, setLoading] = useState({
    crypto: false,
    chart: false
  });

  const formatter = usePriceFormatter();

  // Get color for price change utility
  const getChangeColor = useCallback((value: number) => {
    if (value > 0) return '#4edf6d'; // Green
    if (value < 0) return '#ff2a2a'; // Red
    return '#FFFFFF'; // White/neutral
  }, []);

  const externalID = location.state?.externalID;
  const tradType = location?.state?.tradType || 'Buy';

  // Get the effective currency key (USD, INR, EUR, etc.)
  const currencyKey = currencyInfo.effectiveCurrency || 'USD';

  const symbol = crypto?.symbol || "N/A";
  const name = crypto?.name || "N/A";
  const icon = crypto?.logo;
  const isLoanAvailable = crypto?.isLoanAvailable;

  // Get quote data using the correct currency key
  const quoteData = extractQuoteWithFallback(crypto as CryptoData, currencyKey);
  const price = quoteData?.price;
  const percentChange24h = quoteData?.percent_change_24h;

  const changeAmount = Number(
    calculateChangeAmount(price, percentChange24h).value
  );

  const stats = [
    {
      label: "Market Cap",
      value: quoteData?.market_cap
        ? formatter.display(quoteData.market_cap, { compact: true })
        : "N/A"
    },
    {
      label: "Market Cap Change %",
      value: quoteData?.percent_change_24h !== undefined
        ? formatPercentage(quoteData.percent_change_24h)
        : "N/A"
    },
    {
      label: "Volume (24h)",
      value: quoteData?.volume_24h
        ? formatter.display(quoteData.volume_24h, { compact: true })
        : "N/A"
    },
    {
      label: "Volume Change % (24h)",
      value: quoteData?.volume_change_24h !== undefined
        ? formatPercentage(quoteData.volume_change_24h, { showSign: true })
        : "N/A"
    },
    {
      label: "FDV (Fully Diluted Valuation)",
      value: quoteData?.fully_diluted_market_cap
        ? formatter.display(quoteData.fully_diluted_market_cap, { compact: true })
        : "N/A"
    },
    {
      label: "Vol/Mkt Cap (24h)",
      value: quoteData?.volume_24h && quoteData?.market_cap
        ? formatPercentage(((quoteData.volume_24h / quoteData.market_cap) * 100))
        : "N/A"
    },
    {
      label: "Total Supply",
      value: crypto?.total_supply ? `${formatter.display(crypto.total_supply, { compact: true, showSymbol: false })} ${symbol}` : "N/A"
    },
    {
      label: "Max Supply",
      value: crypto?.max_supply ? `${formatter.display(crypto.max_supply, { compact: true, showSymbol: false })} ${symbol}` : "N/A"
    },
    {
      label: "Circulating Supply",
      value: crypto?.circulating_supply ? `${formatter.display(crypto.circulating_supply, { compact: true, showSymbol: false })} ${symbol}` : "N/A"
    },
  ];

  const customerInfo = getCustomerInfoFromLocalStorage();
  const isPremium = customerInfo?.entitlements?.active?.Premium?.isActive;

  // Fetch crypto details
  const fetchCryptoDetail = useCallback(async () => {
    if (!externalID) return;

    dispatch(startLoading());
    setLoading(prev => ({ ...prev, crypto: true }));
    try {
      const url = UrlConstant.Crypto.GetCryptoMarketDetail.replace("{externalId}", externalID);
      const response = await Axios.get(url);
      const stocks = response.data?.data || [];
      const cryptoData = stocks[0] || null;
      setCrypto(cryptoData);

      if (cryptoData && typeof cryptoData.isAddedToWatchList === 'boolean') {
        setIsWatchlist(cryptoData.isAddedToWatchList);
      } else {
        setIsWatchlist(false);
      }
    } catch (err) {
      console.error("Failed to fetch crypto details:", err);
      setCrypto(null);
      setIsWatchlist(false);
    } finally {
      setLoading(prev => ({ ...prev, crypto: false }));
      dispatch(endLoading());
    }
  }, [externalID, dispatch]);

  const checkAccountStatus = async () => {
    dispatch(startLoading());
    setLoading(prev => ({ ...prev, crypto: true }));
    try {
      const response = await Axios.get(UrlConstant.Sumsub.CheckAccountStatus);
      const resData = response.data;
      if (resData.success === true) {
        setUserStatusData(resData.data);
      }
    } catch (error) {
      console.log(error)
    } finally {
      setLoading(prev => ({ ...prev, crypto: false }));
      dispatch(endLoading());
    }
  }

  // Fetch chart data
  const fetchChartData = useCallback(async () => {
    if (!crypto?.symbol) return;

    dispatch(startLoading());
    setLoading(prev => ({ ...prev, chart: true }));

    try {
      const pair = `${crypto.symbol.toLowerCase()}usd`;
      const { startTime, endTime } = getTimeRangeForSelection(selection);
      const period = getPeriodForSelection(selection);

      // Use the new Wolfpack API endpoint instead of direct SFOX call
      const wolfpackUrl = UrlConstant.Crypto.Candlesticks;
      const params = {
        pair,
        startTime,
        endTime,
        period,
        currency: currencyInfo.effectiveCurrency
      };

      const res = await Axios.get(wolfpackUrl, { params });
      const apiData = res.data?.data || [];

      const lineData = apiData.map((item: any) => ({
        x: new Date(item.start_time * 1000).toISOString(),
        y: parseFloat(item.close_price),
      }));

      const shouldDownsample = selection === "3 Month" || selection === "6 Month" || selection === "1 Year";
      const downsample = <T,>(arr: T[], step = 7): T[] => {
        if (!Array.isArray(arr) || arr.length <= step) return arr;
        const sampled: T[] = arr.filter((_, idx) => idx % step === 0);
        if (arr.length && sampled[sampled.length - 1] !== arr[arr.length - 1]) {
          sampled.push(arr[arr.length - 1]);
        }
        return sampled;
      };

      setChartData(shouldDownsample ? downsample(lineData) : lineData);

      const candleData = apiData.map((item: any) => ({
        x: new Date(item.start_time * 1000).toISOString(),
        y: [
          parseFloat(item.open_price),
          parseFloat(item.high_price),
          parseFloat(item.low_price),
          parseFloat(item.close_price),
        ],
        v: parseFloat(item.volume || 0),
      }));
      setCandleStickChartData(shouldDownsample ? downsample(candleData) : candleData);

      if (lineData && lineData.length > 0) {
        const firstPoint = lineData[0].y as number;
        const lastPoint = lineData[lineData.length - 1].y as number;
        const percentage = firstPoint !== 0 ? ((lastPoint - firstPoint) / firstPoint) * 100 : 0;
        setTrendValue(lastPoint - firstPoint);
        setTrendPercentage(percentage);
      } else {
        setTrendValue(0);
        setTrendPercentage(0);
      }

    } catch (err) {
      console.error("Failed to fetch chart data:", err);
      setChartData([]);
      setCandleStickChartData([]);
      setTrendValue(0);
      setTrendPercentage(0);
    } finally {
      setLoading(prev => ({ ...prev, chart: false }));
      dispatch(endLoading());
    }
  }, [crypto?.symbol, selection, dispatch, currencyInfo.effectiveCurrency]);

  React.useEffect(() => {
    fetchCryptoDetail();
  }, [fetchCryptoDetail]);

  React.useEffect(() => {
    fetchChartData();
  }, [fetchChartData]);

  React.useEffect(() => {
    checkAccountStatus();
  }, []);


  // Event handlers
  const handleReadMore = useCallback((index: number) => {
    setExpandedCards(prev => ({
      ...prev,
      [index]: !prev[index],
    }));
  }, []);

  const handleScrollLeft = useCallback(() => {
    scrollRef.current?.scrollBy({ left: -200, behavior: 'smooth' });
  }, []);

  const handleScrollRight = useCallback(() => {
    scrollRef.current?.scrollBy({ left: 200, behavior: 'smooth' });
  }, []);

  const handleSelectionChange = useCallback((
    event: React.MouseEvent<HTMLElement>,
    newSelection: string | null
  ) => {
    if (newSelection) {
      setSelection(newSelection);
      setChartData([]);
      setCandleStickChartData([]);
    }
  }, []);

  const handleGraphTypeChange = useCallback((
    event: React.MouseEvent<HTMLElement>,
    newGraphType: "line" | "candlestick" | null
  ) => {
    if (newGraphType) {
      setGraphType(newGraphType);
    }
  }, []);


  const onClickAnalysis = useCallback(() => {
    // if (isPremium) {
    setAnalysisPopup(true);
    // } else {
    //   showPremiumAlert(navigate);
    // }
    // }, [isPremium, navigate]);
  }, []);

  const handleCloseAnalysis = () => {
    setAnalysisPopup(false);
  };

  const [showPopup, setShowPopup] = useState(false);
  const [msg, setMsg] = useState("");
  const [Success, setSuccess] = useState(false);
  const [heading, setHeading] = useState("");

  const closePopup = () => setShowPopup(false);

  const handleWatchlistToggle = useCallback(async () => {
    const userId = getCurrentUserId();
    const prevState = isWatchlist;

    const payLoad = {
      userId,
      symbol: externalID,
      type: 'crypto',
    };

    const URL = prevState
      ? UrlConstant.WachList.RemoveWatchList.replace('{0}', externalID)
      : UrlConstant.WachList.CreateWatchList;

    try {
      let response;
      if (prevState) {
        response = await Axios.delete(URL);
        setIsWatchlist(false);
      } else {
        response = await Axios.post(URL, payLoad);
        setIsWatchlist(true);
      }
      const resData = response?.data;
      setSuccess(true);
      setShowPopup(true);
      setMsg(resData?.msg || (prevState ? "Removed from Watchlist" : "Added to Watchlist"));
      setHeading(prevState ? "Removed from Watchlist" : "Added to Watchlist");
    } catch (err) {
      setIsWatchlist(prevState);
      setSuccess(false);
      setShowPopup(true);
      setMsg("Something went wrong. Please try again.");
      setHeading("Error");
    }
  }, [isWatchlist, externalID]);

  const navigateToDashboard = useCallback(() => navigate("/dashboard"), [navigate]);
  const navigateToCryptoMarket = useCallback(() => navigate("/crypto-market"), [navigate]);

  const isLoading = loading.crypto;
  return (
    <Box className="bg-color" display="flex">
      <Container
        maxWidth={false}
        className="bg-color p-3 px-5"
        style={{ height: "fit-content", width: "65%" }}
      >
        {/* Breadcrumb navigation */}
        <Typography className="text-secondary" sx={{ marginTop: "30px", marginBottom: "30px" }}>
          <span onClick={navigateToDashboard} style={{ color: "#4A7AF2", cursor: "pointer" }}>
            Dashboard
          </span>
          {" / "}
          <span onClick={navigateToCryptoMarket} style={{ color: "#4A7AF2", cursor: "pointer" }}>
            Crypto
          </span>
          {" / "}
          <span>{name}</span>
        </Typography>

        <Container style={{ width: "100%", left: "0", margin: "0" }} className="p-0">
          {/* Header section */}
          <Box>
            <Box display="flex" alignItems="start" justifyContent="space-between">
              {/* Crypto info */}
              <Box className="text-white">
                <Box display="flex" alignItems="top" gap="10px" mb={2}>
                  {icon ? (
                    <Avatar
                      alt={symbol}
                      src={icon}
                      sx={{ width: 64, height: 64, backgroundColor: "white", objectFit: "fill" }}
                      className="icon"
                    />
                  ) : (
                    <Box
                      sx={{
                        width: 64,
                        minWidth: 64,
                        height: 64,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "#f0f0f0",
                        borderRadius: "50%",
                      }}
                    >
                      <Typography variant="h6" sx={{ fontWeight: "bold", color: "#212529" }}>
                        {symbol}
                      </Typography>
                    </Box>
                  )}
                  <Box>
                    <Typography mb={1} variant="h5">
                      {name}
                    </Typography>
                    <Typography variant="body1" className="text-secondary">
                      {symbol}
                    </Typography>
                  </Box>
                  {isLoanAvailable && (
                    <Tooltip title="Booster Loan Eligible" arrow placement="top">
                      <span>
                        <ThunderIconCirlcle style={{ marginTop: '3px' }} width={20} height={20} />
                      </span>
                    </Tooltip>
                  )}
                </Box>
              </Box>

              {/* Action buttons */}
              <Box display="flex" flexDirection="column" justifyContent="end">
                <Box display="flex" minWidth="300px" justifyContent="end">
                  <ToggleButton
                    className="text-secondary"
                    value="watchlist"
                    sx={{
                      padding: "7px 15px !important",
                      borderRadius: "10px !important",
                      borderColor: "#FFFFFF33 !important",
                    }}
                    onClick={handleWatchlistToggle}
                  >
                    {isWatchlist ? <BookmarkOutlinedIcon /> : <BookmarkBorderOutlined />}
                    Watchlist
                  </ToggleButton>
                  <Button
                    variant="contained"
                    color="primary"
                    className="btn-primary btn"
                    sx={{
                      padding: "7px 15px !important",
                      borderRadius: "10px !important",
                      borderColor: "#FFFFFF33 !important",
                    }}
                    onClick={onClickAnalysis}
                    startIcon={
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M8.17666 16.2012C8.68476 16.2012 9.17956 16.0438 9.59839 15.7517C10.0169 15.4597 10.34 15.0471 10.53 14.5683C10.7199 14.0896 10.7693 13.5641 10.6727 13.0573C10.5761 12.5505 10.3373 12.0821 9.98317 11.7124C9.62886 11.3425 9.17479 11.088 8.67711 10.9847C8.17926 10.8813 7.66347 10.9346 7.19594 11.1368C6.7287 11.3388 6.33283 11.6794 6.05588 12.1121C5.77921 12.5444 5.63267 13.0504 5.6325 13.5663C5.63138 13.9096 5.69528 14.2502 5.82098 14.5687C5.9468 14.8874 6.13234 15.1785 6.36792 15.4244C6.60354 15.6704 6.88465 15.8664 7.19564 16.0002C7.50609 16.1337 7.83955 16.2023 8.17666 16.2012ZM8.17666 16.2012V15.7012L8.1786 16.2012C8.17795 16.2012 8.17731 16.2012 8.17666 16.2012ZM13.9809 20.759H9.99369C9.61302 20.759 9.25253 20.6009 8.9902 20.327C8.72858 20.0539 8.58518 19.6879 8.58518 19.3105C8.58518 18.9331 8.72858 18.5671 8.9902 18.294C9.25253 18.0202 9.61302 17.8621 9.99369 17.8621L13.9544 17.8621C13.9546 17.8621 13.9549 17.8621 13.9551 17.8621H13.9558V18.3621L13.9809 20.759ZM13.9809 20.759C13.9809 20.759 13.981 20.759 13.9811 20.759C14.1692 20.759 14.3548 20.7196 14.5267 20.6439C14.6986 20.5681 14.8528 20.4577 14.9806 20.3204C15.1084 20.1832 15.2075 20.0218 15.273 19.8462C15.3376 19.6731 15.3685 19.489 15.3644 19.3043L13.9809 20.759ZM15.7971 16.2012C15.2894 16.201 14.7949 16.0436 14.3764 15.7517C13.9578 15.4597 13.6348 15.0471 13.4448 14.5683C13.2549 14.0896 13.2055 13.5641 13.3021 13.0573C13.3986 12.5505 13.6375 12.0821 13.9916 11.7124C14.3459 11.3425 14.8 11.088 15.2977 10.9847C15.7955 10.8813 16.3113 10.9346 16.7788 11.1368C17.2461 11.3388 17.6419 11.6794 17.9189 12.1121C18.1957 12.5446 18.3423 13.051 18.3423 13.5672M15.7971 16.2012L18.3423 13.5672M15.7971 16.2012C16.1346 16.2024 16.4684 16.1339 16.7791 16.0002C17.0901 15.8664 17.3712 15.6704 17.6068 15.4244C17.8424 15.1785 18.028 14.8874 18.1538 14.5687L17.6887 14.3851M15.7971 16.2012C15.7975 16.2012 15.7978 16.2012 15.7981 16.2012V15.7012L15.7962 16.2012C15.7965 16.2012 15.7968 16.2012 15.7971 16.2012ZM15.7971 16.2012L17.6887 14.3851M18.3423 13.5672C18.3433 13.9102 18.2794 14.2505 18.1538 14.5687M18.3423 13.5672L18.1538 14.5687M17.6887 14.3851L18.1538 14.5687M17.6887 14.3851L18.1538 14.5687M21.4811 12.4871C21.4811 12.7632 21.7049 12.9871 21.9811 12.9871H22.607C22.8327 12.9922 23.0523 13.0869 23.2199 13.2586C23.3874 13.4303 23.4895 13.666 23.5 13.9191L23.5 17.2556L23.5 17.2574C23.5004 17.3813 23.4773 17.5037 23.4325 17.6173L23.8976 17.8008L23.4325 17.6173C23.3877 17.7308 23.3224 17.8325 23.2413 17.9172C23.1602 18.0019 23.0651 18.0677 22.962 18.112C22.8589 18.1564 22.7493 18.1787 22.6392 18.1782H22.6372H22.0063C21.7302 18.1782 21.5063 18.4021 21.5063 18.6782V21.1538C21.5052 21.7825 21.2653 22.381 20.8455 22.8192C20.4263 23.2568 19.8629 23.4989 19.2798 23.5H4.72021C4.13713 23.4989 3.57366 23.2568 3.15454 22.8192L2.79347 23.1651L3.15454 22.8192C2.7348 22.381 2.49491 21.7827 2.4937 21.1542V18.6782C2.4937 18.4021 2.26984 18.1782 1.9937 18.1782L1.36278 18.1782L1.36084 18.1782C1.25074 18.1787 1.14111 18.1564 1.03805 18.1121C0.934941 18.0677 0.839811 18.0019 0.758739 17.9172C0.677623 17.8326 0.612274 17.7308 0.56747 17.6173C0.522655 17.5037 0.499564 17.3813 0.500009 17.2574V17.2556L0.500013 13.9097L0.500006 13.9079C0.499564 13.784 0.522655 13.6616 0.56747 13.548C0.612274 13.4345 0.677623 13.3328 0.758739 13.2481C0.839811 13.1634 0.934941 13.0976 1.03805 13.0533C1.14111 13.0089 1.25074 12.9866 1.36084 12.9871H1.36278H1.96846C2.2446 12.9871 2.46846 12.7632 2.46846 12.4871V10.2219C2.46967 9.59336 2.70957 8.99503 3.1293 8.55685C3.54832 8.11943 4.11159 7.87733 4.69451 7.87607H11.0789C11.355 7.87607 11.5789 7.65222 11.5789 7.37607V5.03133C11.5789 4.82819 11.456 4.64523 11.2679 4.56844C10.8966 4.41682 10.5739 4.15059 10.3445 3.80078C10.115 3.45089 9.99027 3.03482 9.98896 2.6067C9.98876 2.21959 10.0898 1.841 10.2794 1.5119C10.4691 1.1828 10.7395 0.91683 11.0593 0.740206C11.3788 0.563699 11.7367 0.482337 12.0949 0.503202C12.453 0.52407 12.8008 0.646578 13.1006 0.859855C13.4006 1.07328 13.6417 1.37005 13.7956 1.72016C13.9496 2.07039 14.0095 2.45906 13.9679 2.84378C13.9264 3.22847 13.7852 3.59215 13.5619 3.89594C13.3386 4.19957 13.0422 4.43119 12.7065 4.56859C12.5186 4.64549 12.3959 4.82834 12.3959 5.03133V7.37607C12.3959 7.65222 12.6198 7.87607 12.8959 7.87607H19.255C19.8379 7.87733 20.4012 8.11943 20.8202 8.55685C21.24 8.99512 21.48 9.5936 21.4811 10.2223V12.4871Z" stroke="white" strokeLinejoin="round" />
                      </svg>
                    }
                  >
                    Ai Analysis
                  </Button>
                </Box>
              </Box>
            </Box>

            {/* Price and trend */}
            <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
              <Box display="flex" gap="10px" className="text-white" alignItems="center">
                <Typography variant="h5" textAlign="end">
                  {price !== undefined && price !== null ? formatter.display(price) : 'N/A'}
                </Typography>
                <Typography
                  variant="h5"
                  style={{ color: getChangeColor(changeAmount) }}
                >
                  {formatter.display(changeAmount, { showSign: true })} ({formatPercentage(percentChange24h, { showSign: true })})
                </Typography>
              </Box>

              {/* Chart type toggle */}
              <ToggleButtonGroup
                value={graphType}
                exclusive
                onChange={handleGraphTypeChange}
                aria-label="Chart type"
              >
                <ToggleButton value="line" style={{ width: "50px", height: "35px", padding: "10px" }}>
                  <img src={LinChartIcon} alt="Line Chart" style={{ width: "22px" }} />
                </ToggleButton>
                <ToggleButton value="candlestick" style={{ width: "50px", height: "35px", padding: "10px" }}>
                  <img src={CandleStickIcon} alt="Candlestick Chart" style={{ width: "22px" }} />
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Box>

          {/* Chart section */}
          <Box className="dark-tbl no-border p-0 mt-4">
            <Card className="custom-card overflow-hidden bg-color w-100" style={{ height: "fit-content" }}>
              <Card.Body className="ps-12 p-2 h-100">
                <Box width="100%" display="flex" justifyContent="flex-end" alignItems="center" mb={2}>
                  <Box bgcolor={trendValue > 0 ? "#4EDF6D1A" : "#FF2A2A1A"} borderRadius={50} p="0 10px">
                    <Typography variant="body1" className="text-white" p={1}>
                      {selection}{' '}
                      <span style={{ color: getChangeColor(trendValue) }}>
                        {formatter.display(trendValue, { showSign: true })} ({formatPercentage(trendPercentage, { showSign: true })})
                      </span>
                    </Typography>
                  </Box>
                </Box>

                {/* Chart */}
                {graphType === "line" ? (
                  <LineChart
                    data={chartData}
                    timeline={selection}
                    lineColor={getChangeColor(trendValue)}
                  />
                ) : (
                  <CandlestickChart
                    data={candleStickChartData}
                    timeline={selection}
                  />
                )}

                <hr style={{ borderColor: "#FFFFFF", borderWidth: "1px" }} />

                {/* Time interval selection */}
                <Box mb={1} display="flex" justifyContent="space-between" alignItems="center">
                  <ToggleButtonGroup
                    value={selection}
                    exclusive
                    onChange={handleSelectionChange}
                    aria-label="Time interval"
                  >
                    <ToggleButton value="1 Day">1 Day</ToggleButton>
                    <ToggleButton value="5 Day">5 Day</ToggleButton>
                    <ToggleButton value="1 Month">1 Month</ToggleButton>
                    <ToggleButton value="3 Month">3 Month</ToggleButton>
                    <ToggleButton value="6 Month">6 Month</ToggleButton>
                    <ToggleButton value="1 Year">1 Year</ToggleButton>
                    {/* <ToggleButton value="5 Years">5 Years</ToggleButton>
                    <ToggleButton value="All">Max</ToggleButton> */}
                  </ToggleButtonGroup>
                </Box>
              </Card.Body>
            </Card>
          </Box>
        </Container>

        {/* Market Stats */}
        <Box my={5} pt={2}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography className="text-white" mb={2} style={{ fontSize: "1.5rem" }}>
              Market Stats
            </Typography>
          </Box>
          <Box>
            <Box display="flex" alignItems="center" gap="10px" width="100%" flexWrap="wrap">
              {stats.map((item, index) => (
                <Box key={index}>
                  <Card className="card" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', minWidth: "190px", height: '100%' }}>
                    <CardContent className="text-white">
                      <Typography gutterBottom>{item.label}</Typography>
                      <Typography mb="0px" mr={8}>{item.value}</Typography>
                    </CardContent>
                  </Card>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
        {/* Insights */}
        {/* <Box my={5} pt={2}>
          <Typography className="text-white" mb={2} style={{ fontSize: "1.5rem" }}>
            Insights
          </Typography>
          <Box display='flex' alignItems='center' justifyContent='space-between' width='100%'>
            <IconButton sx={{ pl: "0px" }} onClick={handleScrollRight}>
              <ExpandCircleDownOutlined sx={{ rotate: "90deg" }} />
            </IconButton>
            <Box
              sx={{ "&::-webkit-scrollbar": { display: "none" } }}
              style={{ scrollBehavior: "smooth", overflowX: "auto" }}
              className="scroll"
              display="flex"
              gap="20px"
              width="95%"
              overflow="hidden"
              ref={scrollRef}
            >
              {insights.length
                ? insights.map((item: any, index: number) => (
                  <Card key={index} className="card" style={{ minWidth: "530px", maxWidth: "530px", height: "fit-content" }}>
                    <CardContent className="text-white">
                      <Typography gutterBottom>{item.title}</Typography>
                      <Typography variant="body2" gutterBottom className="text-secondary">
                        {moment(item.publishedOn).format("MMMM DD, YYYY")}
                      </Typography>
                      <Typography
                        variant="body2"
                        style={{
                          overflow: "hidden",
                          height: expandedCards[index] ? "fit-content" : "40px",
                          transition: "max-height 0.3s ease",
                        }}
                      >
                        {item.summary}
                      </Typography>
                      <span
                        onClick={() => handleReadMore(index)}
                        style={{ color: "rgba(74, 122, 242, 1)", cursor: "pointer" }}
                      >
                        {expandedCards[index] ? "show less" : "read more"}
                      </span>
                    </CardContent>
                  </Card>
                ))
                : null}
            </Box>
            <IconButton sx={{ pl: "5px" }} onClick={handleScrollLeft}>
              <ExpandCircleDownOutlined sx={{ rotate: "-90deg" }} />
            </IconButton>
          </Box>
        </Box> */}
        {/* About section */}
        <Box my={5} pt={2}>
          <Typography className="text-white" mb={2} style={{ fontSize: "1.5rem" }}>
            About
          </Typography>
          <Box>
            <Card className="card" style={{ minWidth: '530px', maxWidth: "100%" }}>
              <CardContent className="text-white">
                <Typography gutterBottom>{name}</Typography>
                <Typography variant="body2" gutterBottom className="text-secondary">
                  {moment(crypto?.last_updated).format("MMMM DD, YYYY")}
                </Typography>
                <Typography variant="body2">
                  {crypto?.description || "No description available."}
                </Typography>
              </CardContent>
            </Card>
          </Box>
        </Box>
      </Container>

      {/* Right sidebar */}
      <Container style={{ width: "35%", paddingTop: "20px" }}>
        <Box width="100%">
          <Box width="440px" height="fit-content" overflow="hidden">
            {userStatusData?.isFullyCompleted === true ? (
              <>
                <Typography
                  component="h1"
                  fontSize={24}
                  className="text-white"
                  mb={2}
                >
                  Buy/Sell
                </Typography>
                <BuySell
                  stockDetail={{
                    ...crypto,
                    displayName: crypto?.name,
                  }}
                  // c2cTradable={crypto?.c2cTradable ?? false}
                  tickerData={{
                    lastPrice: price ?? 0,
                    previousClose: (price ?? 0) / (1 + (percentChange24h ?? 0) / 100),
                    diffAmount: changeAmount,
                    percentage: percentChange24h ?? 0,
                  }}
                  tickerDataAll={crypto}
                  icon={crypto?.logo}
                  tradType={tradType}
                  assetType="crypto"
                />
              </>
            ) : (
              <RightSideSection />
            )}
          </Box>
        </Box>
      </Container>

      {/* Loading overlay */}
      {isLoading && (
        <Box
          position="fixed"
          top={0}
          left={0}
          width="100vw"
          height="100vh"
          bgcolor="rgba(0, 0, 0, 0.5)"
          display="flex"
          justifyContent="center"
          alignItems="center"
          style={{ backdropFilter: "blur(5px)", zIndex: 9999 }}
        >
          <CircularProgress color="primary" />
        </Box>
      )}
      {showPopup && <Successpopup Heading={heading} Success={Success} onClose={closePopup} Message={msg} />}
      {analysisPopup && crypto && (
        <AiAnalysis
          stockDetail={{ ...crypto, displayName: crypto?.name, stockExchangeCode: '', symbol: crypto?.symbol, assetType: "crypto" }}
          symbol={crypto?.symbol ?? ""}
          onClose={handleCloseAnalysis}
          icon={crypto?.logo}
          tickerData={{
            lastPrice: price ?? 0,
            previousClose: (price ?? 0) / (1 + (percentChange24h ?? 0) / 100),
            diffAmount: (price ?? 0) - ((price ?? 0) / (1 + (percentChange24h ?? 0) / 100)),
            percentage: percentChange24h ?? 0,
          }}
          isWatchlist={isWatchlist}
          CreateWatchList={handleWatchlistToggle}
        />
      )}
    </Box>
  );
}

export default CryptoDetail;